import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DocumentDetail } from "@/briefpoint-client";
import { useCaseApi, useDocumentApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { getCaseOptions, getDocumentOptions } from "@/lib/query-options";
import type { CaseResponse } from "@/lib/types";

export type PluralPartyField = "respondingParty" | "propoundingParty";

const fieldNameMap = {
  respondingParty: "respondingPartyIsPlural",
  propoundingParty: "propoundingPartyIsPlural",
} as const;

export function usePluralPartyToggle(
  field: PluralPartyField,
  documentId?: string,
  caseId?: string,
) {
  const auth = useAuth();
  const firmId = auth.user?.firmId;
  const documentApi = useDocumentApi();
  const caseApi = useCaseApi();
  const queryClient = useQueryClient();

  const fieldName = fieldNameMap[field];

  const documentQueryOptions = getDocumentOptions(
    documentId ?? "",
    documentApi,
  );
  const documentQuery = useQuery({
    ...documentQueryOptions,
    enabled: !!documentId,
  });

  const caseQueryOptions = getCaseOptions(caseApi, firmId, caseId);
  const caseQuery = useQuery({
    ...caseQueryOptions,
    enabled: !!caseId && !!firmId,
  });

  const value =
    documentQuery.data?.info?.[fieldName] ??
    caseQuery.data?.[fieldName] ??
    false;

  const updateDocumentMutation = useMutation({
    mutationFn: async (newValue: boolean) => {
      if (!documentId) return;
      const currentDoc = queryClient.getQueryData<DocumentDetail>(
        documentQueryOptions.queryKey,
      );
      if (!currentDoc) return;
      await documentApi.documentUpdateDocument({
        documentId,
        document: {
          ...currentDoc,
          info: currentDoc.info
            ? { ...currentDoc.info, [fieldName]: newValue }
            : currentDoc.info,
        },
      });
    },
    onMutate: async (newValue: boolean) => {
      if (!documentId) return { previousDocument: undefined };
      await queryClient.cancelQueries({
        queryKey: documentQueryOptions.queryKey,
      });
      const previousDocument = queryClient.getQueryData<DocumentDetail>(
        documentQueryOptions.queryKey,
      );
      queryClient.setQueryData<DocumentDetail>(
        documentQueryOptions.queryKey,
        (prev: DocumentDetail | undefined) => {
          if (!prev) return prev;
          return {
            ...prev,
            info: prev.info
              ? { ...prev.info, [fieldName]: newValue }
              : prev.info,
          };
        },
      );
      return { previousDocument };
    },
    onError: (
      _error: Error,
      _newValue: boolean,
      context: { previousDocument?: DocumentDetail } | undefined,
    ) => {
      if (context?.previousDocument) {
        queryClient.setQueryData(
          documentQueryOptions.queryKey,
          context.previousDocument,
        );
      }
    },
    onSettled: () => {
      if (documentId) {
        queryClient.invalidateQueries({
          queryKey: documentQueryOptions.queryKey,
        });
      }
    },
  });

  const updateCaseMutation = useMutation({
    mutationFn: async (newValue: boolean) => {
      if (!caseId || !firmId) return;
      const currentCase = queryClient.getQueryData<CaseResponse>(
        caseQueryOptions.queryKey,
      );
      if (!currentCase) return;
      await caseApi.casePut({
        firmId,
        id: caseId,
        _case: { ...currentCase, [fieldName]: newValue },
      });
    },
    onMutate: async (newValue: boolean) => {
      if (!caseId) return { previousCase: undefined };
      await queryClient.cancelQueries({ queryKey: caseQueryOptions.queryKey });
      const previousCase = queryClient.getQueryData<CaseResponse>(
        caseQueryOptions.queryKey,
      );
      queryClient.setQueryData<CaseResponse>(
        caseQueryOptions.queryKey,
        (prev: CaseResponse | undefined) => {
          if (!prev) return prev;
          return { ...prev, [fieldName]: newValue };
        },
      );
      return { previousCase };
    },
    onError: (
      _error: Error,
      _newValue: boolean,
      context: { previousCase?: CaseResponse } | undefined,
    ) => {
      if (context?.previousCase) {
        queryClient.setQueryData(
          caseQueryOptions.queryKey,
          context.previousCase,
        );
      }
    },
    onSettled: () => {
      if (caseId) {
        queryClient.invalidateQueries({ queryKey: caseQueryOptions.queryKey });
      }
    },
  });

  function onToggle() {
    const newValue = !value;
    if (documentId) updateDocumentMutation.mutate(newValue);
    if (caseId) updateCaseMutation.mutate(newValue);
    return newValue;
  }

  const isPending =
    updateDocumentMutation.isPending || updateCaseMutation.isPending;

  const isLoading = documentQuery.isLoading || caseQuery.isLoading;

  return { value, onToggle, isPending, isLoading };
}
